import { isCycleAudienceAction } from "#shared/cycleAudienceSchemas.ts";
import { isCycleScheduleAction } from "#shared/cycleScheduleSchemas.ts";
import { reviewCyclesContract } from "#shared/reviewCyclesContract.ts";
import { createORPCClient, isDefinedError } from "@orpc/client";
import type { ContractRouterClient } from "@orpc/contract";
import { OpenAPILink } from "@orpc/openapi-client/fetch";
import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import { apiFetch } from "@packages/core-client/api/apiFetch.ts";
import { invalidate, queryPath } from "@packages/core-client/query/queryKeys.ts";
import { useEnvironmentVariable } from "@squide/firefly";
import { useMemo } from "react";

function createReviewCyclesClient(baseUrl: string) {
    const link = new OpenAPILink(reviewCyclesContract, {
        url: `${baseUrl}/api/review-cycles`,
        fetch: apiFetch
    });

    const client: ContractRouterClient<typeof reviewCyclesContract> = createORPCClient(link);
    const queryUtils = createTanstackQueryUtils(client, { path: queryPath("admin-settings", ["review-cycles"]) });

    return { client, queryUtils };
}

export function useReviewCyclesClient() {
    const apiBaseUrl = useEnvironmentVariable("apiBaseUrl");

    return useMemo(() => createReviewCyclesClient(apiBaseUrl), [apiBaseUrl]);
}

export function useSendCycleRemindersMutationOptions() {
    const { queryUtils } = useReviewCyclesClient();

    return queryUtils.sendCycleReminders.mutationOptions({
        meta: { skipGlobalErrorHandler: true },
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "review-cycles")
    });
}

export function useCreateReviewCycleMutationOptions() {
    const { queryUtils } = useReviewCyclesClient();

    return queryUtils.createReviewCycle.mutationOptions({
        meta: { skipGlobalErrorHandler: true },
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "review-cycles")
    });
}

export function usePatchReviewCycleMutationOptions() {
    const { queryUtils } = useReviewCyclesClient();

    return queryUtils.patchReviewCycle.mutationOptions({
        meta: { skipGlobalErrorHandler: true },
        onSuccess: (_data, { actions }, _onMutateResult, { client: queryClient }) => {
            const isAudienceChanged = actions.some(isCycleAudienceAction);
            const areEntryDatesChanged = isAudienceChanged || actions.some(isCycleScheduleAction);

            return invalidate(queryClient, "review-cycles", { except: [...(isAudienceChanged ? [] : [queryUtils.getCycleAudience.key()]), ...(areEntryDatesChanged ? [] : [queryUtils.searchCycleAudiencePreview.key()])] });
        },
        onError: (error, _variables, _onMutateResult, { client: queryClient }) => {
            if (isDefinedError(error) && error.code === "VALIDATION_FAILED" && error.data.cycle) {
                void invalidate(queryClient, "review-cycles");
            }
        }
    });
}

export function useDuplicateReviewCycleMutationOptions() {
    const { queryUtils } = useReviewCyclesClient();

    return queryUtils.duplicateReviewCycle.mutationOptions({
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "review-cycles")
    });
}

export function useRemoveCycleRevieweesMutationOptions() {
    const { queryUtils } = useReviewCyclesClient();

    return queryUtils.removeCycleReviewees.mutationOptions({
        meta: { skipGlobalErrorHandler: true },
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "review-cycles")
    });
}

export function useAddCycleRevieweesMutationOptions() {
    const { queryUtils } = useReviewCyclesClient();

    return queryUtils.addCycleReviewees.mutationOptions({
        meta: { skipGlobalErrorHandler: true },
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "review-cycles")
    });
}

export function useDeleteReviewCycleMutationOptions() {
    const { queryUtils } = useReviewCyclesClient();

    return queryUtils.deleteReviewCycle.mutationOptions({
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "review-cycles")
    });
}

export function useShareCycleReviewsMutationOptions() {
    const { queryUtils } = useReviewCyclesClient();

    return queryUtils.shareCycleReviews.mutationOptions({
        meta: { skipGlobalErrorHandler: true },
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "review-cycles")
    });
}

export function useLockReviewCycleMutationOptions() {
    const { queryUtils } = useReviewCyclesClient();

    return queryUtils.lockReviewCycle.mutationOptions({
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "review-cycles")
    });
}

export function useLaunchReviewCycleMutationOptions() {
    const { queryUtils } = useReviewCyclesClient();

    return queryUtils.launchReviewCycle.mutationOptions({
        meta: { skipGlobalErrorHandler: true },
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "review-cycles")
    });
}

export function useUnshareCycleReviewsMutationOptions() {
    const { queryUtils } = useReviewCyclesClient();

    return queryUtils.unshareCycleReviews.mutationOptions({
        meta: { skipGlobalErrorHandler: true },
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "review-cycles")
    });
}

export function useUnlockReviewCycleMutationOptions() {
    const { queryUtils } = useReviewCyclesClient();

    return queryUtils.unlockReviewCycle.mutationOptions({
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "review-cycles")
    });
}

export function useSetCycleTemporaryManagerMutationOptions() {
    const { queryUtils } = useReviewCyclesClient();

    return queryUtils.setCycleTemporaryManager.mutationOptions({
        meta: { skipGlobalErrorHandler: true },
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "review-cycles")
    });
}

export function useSetCyclePeerReviewersMutationOptions() {
    const { queryUtils } = useReviewCyclesClient();

    return queryUtils.setCyclePeerReviewers.mutationOptions({
        meta: { skipGlobalErrorHandler: true },
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "review-cycles")
    });
}

export function useSetCycleUpwardReviewersMutationOptions() {
    const { queryUtils } = useReviewCyclesClient();

    return queryUtils.setCycleUpwardReviewers.mutationOptions({
        meta: { skipGlobalErrorHandler: true },
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "review-cycles")
    });
}
